---
title: Manage your product catalog
summary: Tag products with allergens, dietary info, and keywords, and take a product off the menu without deleting it.
visibility: authenticated
feature: commerce
audience: author-admin
section: How-to
order: 63
last_verified: 2026-07-12
owner: docs
---

# Manage your product catalog

**Admin → Products** is where you manage the items your expert can sell or recommend in
ordering conversations. Two things you can set on each product — its attributes and its
availability — change how the AI talks about it.

## Tag products with attributes

Open a product and add attribute chips in three categories. Pick from the curated
suggestions or type your own — free-form entries work in any language.

- **Allergens / contains** — things a customer needs to be warned about, like `dairy`,
  `peanuts`, or `gluten`. When someone asks "does the cheesecake have nuts?", the AI
  answers from what you've tagged here.
- **Dietary** — things the AI can recommend a product *for*, like `vegan`, `sugar-free`,
  or `keto`. Ask "what do you have that's sugar-free?" and the AI points to matching
  products.
- **Keywords** — extra search terms and alternate names that help the AI recognize what a
  customer means, even if they don't use the product's exact name.

These apply in both browsing and ordering conversations, across text, voice, and WhatsApp.

If a product was created earlier with old-style flat tags, the product form
shows them as **Legacy tags (replaced on save)**. They already behave like
attributes automatically — and the first time you save the product from the
form, they're converted into the categorized chips above for good, so anything
you clear stays cleared.

## Take a product off the menu

Each product has an **Available** switch — in the product table and on the product form.
Turning it off doesn't delete the product; it just stops the AI from actively offering it:

- It no longer appears in menus, recommendations, or the ordering flow.
- If a customer asks for it **by name** anyway, the AI tells them honestly that it's
  temporarily unavailable and keeps helping with something else — it doesn't just go
  silent or make something up.
- In ordering conversations, your team also gets a note in the Inbox with an unread badge
  so someone can follow up — the AI keeps the conversation going itself, it doesn't hand
  off to a human. Outside an order (a browsing or general question), the customer still
  gets the honest unavailable answer above, just without the Inbox note.

Flip **Available** back on any time to bring the product back into rotation; nothing about
the product's attributes, price, or history is lost while it's off.

## See also

- [Add a workflow to a persona](/help/advanced/add-workflows) — how ordering conversations
  are wired up.
- [Manage your asset library](/help/how-to/manage-your-asset-library) — tagging files the
  AI can send, rather than products.
